import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { FadeInUp, FadeInLeft, FadeInRight, StaggerContainer, StaggerItem } from '../Components/Animations'

// Design tokens
const colors = {
  primary: '#0d9488',
  primaryLight: '#14b8a6',
  primaryDark: '#0f766e',
  secondary: '#0891b2',
  accent: '#5eead4',
  dark: '#0f172a',
  darkBlue: '#1e3a5f',
  text: '#334155',
  textLight: '#64748b',
  white: '#ffffff',
  background: '#f8fafc',
  backgroundAlt: '#f0fdfa'
}

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${colors.white};
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
`

const MaxWidthContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

// Hero Section
const HeroSection = styled.section`
  padding: 160px 0 80px;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(8, 145, 178, 0.15) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    padding: 120px 0 60px;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`

const HeroLabel = styled.span`
  display: inline-block;
  background: rgba(20, 184, 166, 0.2);
  color: ${colors.accent};
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 24px;
`

const HeroTitle = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: ${colors.white};
  margin-bottom: 24px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`

const HeroSubtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

// Main Section
const MainSection = styled.section`
  padding: 100px 0;
  background: ${colors.background};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`

// Contact Info
const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const InfoCard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  gap: 20px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  }
`

const InfoIcon = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`

const InfoContent = styled.div`
  flex: 1;
`

const InfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 8px;
`

const InfoText = styled.p`
  font-size: 15px;
  color: ${colors.textLight};
  line-height: 1.7;
  margin: 0;

  a {
    color: ${colors.primary};
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;

    &:hover {
      color: ${colors.primaryDark};
    }
  }
`

// Form Section
const FormCard = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 48px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`

const FormTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${colors.dark};
  margin-bottom: 8px;
`

const FormSubtitle = styled.p`
  font-size: 16px;
  color: ${colors.textLight};
  margin-bottom: 32px;
`

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text};
`

const Input = styled.input`
  font-family: inherit;
  font-size: 16px;
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: ${colors.background};

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    background: ${colors.white};
    box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`

const TextArea = styled.textarea`
  font-family: inherit;
  font-size: 16px;
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  min-height: 140px;
  resize: vertical;
  transition: all 0.3s ease;
  background: ${colors.background};

  &:focus {
    outline: none;
    border-color: ${colors.primary};
    background: ${colors.white};
    box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  color: white;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  padding: 16px 40px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-start;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 40px rgba(13, 148, 136, 0.3);
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

// Map Section
const MapSection = styled.section`
  padding: 0;
`

const MapContainer = styled.div`
  height: 400px;
  background: ${colors.dark};
  position: relative;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    filter: grayscale(20%);
  }
`

const RSNGContact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    console.log('Form submitted:', formData)
    alert('Thank you for contacting us! We will get back to you soon.')
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: ''
    })
  }

  const contactInfo = [
    {
      icon: '📍',
      title: 'Hyderabad, India',
      text: '605 B Block, Asian Sun City, Backside of Sharath City Capital Mall, Kondapur 500084'
    },
    {
      icon: '📍',
      title: 'Visakhapatnam, India',
      text: '10 A (Part), SDF-1 Building, Phase-I, VSEZ, Duvvada, Visakhapatnam, AP 530049'
    },
    {
      icon: '📍',
      title: 'Wyoming, USA',
      text: '1309 Coffeen AVE STE 1200, Sheridan, WY 82801'
    },
    {
      icon: '✉️',
      title: 'Email Us',
      text: '<a href="mailto:info@rsnginfo.com">info@rsnginfo.com</a>'
    },
    {
      icon: '🕐',
      title: 'Business Hours',
      text: 'Monday - Friday: 9:00 AM - 6:00 PM IST'
    }
  ]

  return (
    <Container>
      {/* Hero Section */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <FadeInUp>
              <HeroLabel>Contact Us</HeroLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <HeroTitle>Let's Build Something Great Together</HeroTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <HeroSubtitle>
                Connect with our team to discuss innovative solutions and tailored strategies that will empower your business.
              </HeroSubtitle>
            </FadeInUp>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      {/* Main Section */}
      <MainSection>
        <MaxWidthContainer>
          <ContentGrid>
            {/* Contact Info */}
            <FadeInLeft>
              <ContactInfo>
                <StaggerContainer staggerDelay={0.1}>
                  {contactInfo.map((info, index) => (
                    <StaggerItem key={index}>
                      <InfoCard>
                        <InfoIcon>{info.icon}</InfoIcon>
                        <InfoContent>
                          <InfoTitle>{info.title}</InfoTitle>
                          <InfoText dangerouslySetInnerHTML={{ __html: info.text }} />
                        </InfoContent>
                      </InfoCard>
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              </ContactInfo>
            </FadeInLeft>

            {/* Contact Form */}
            <FadeInRight delay={0.2}>
              <FormCard>
                <FormTitle>Send Us a Message</FormTitle>
                <FormSubtitle>Fill out the form below and we'll get back to you shortly.</FormSubtitle>

                <ContactForm onSubmit={handleSubmit}>
                  <FormRow>
                    <FormGroup>
                      <Label>Full Name *</Label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Email Address *</Label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormRow>
                    <FormGroup>
                      <Label>Phone Number</Label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Company Name</Label>
                      <Input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label>Message *</Label>
                    <TextArea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your project or requirements..."
                    />
                  </FormGroup>

                  <SubmitButton type="submit">
                    Send Message
                    <span>→</span>
                  </SubmitButton>
                </ContactForm>
              </FormCard>
            </FadeInRight>
          </ContentGrid>
        </MaxWidthContainer>
      </MainSection>

      {/* Map Section */}
      <MapSection>
        <MapContainer>
          <iframe
            src="https://maps.google.com/maps?q=17.458960649995028,78.36414459721613&z=17&output=embed"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="RSNG Info Solutions - 605 B Block, Asian Sun City, Kondapur"
          />
        </MapContainer>
      </MapSection>
    </Container>
  )
}

export default RSNGContact
